import React from "react";
import { connect } from "react-redux";
import { Modal, Grid, Button, Header } from "semantic-ui-react";
import {
  setCreateAssetModal,
  showSuccessAlert as showSuccessAlertAction,
} from "@actions/modals";
import {
  newCategory,
  updateCategory,
  newGameTitle,
  updateGameTitle,
  newManufacturer,
  updateManufacturer,
  newSupplier,
  updateSupplier,
  newCabinetType,
  updateCabinetType,
  newDepot,
  updateDepot,
  newNoteAcceptorModel,
  updateNoteAcceptorModel,
} from "@actions/index";
import BaseSettings from "@components/systemAdmin/BaseSettings";
import OptionsList from "@components/systemAdmin/OptionsList";

class CreateAssetModal extends BaseSettings {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      selectedOptions: {
        main: [],
        ownership: [],
        operational: [],
      },
      loading: false,
      saving: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { operatorId, type, open } = this.props;
    if (!operatorId || !type) return;

    const justOpened = open && !prevProps.open;
    const typeOrOperatorChanged =
      type !== prevProps.type || operatorId !== prevProps.operatorId;
    const shouldResetAndFetch = open && (justOpened || typeOrOperatorChanged);

    if (shouldResetAndFetch) {
      this.setState(
        {
          selectedOptions: {
            main: [],
            ownership: [],
            operational: [],
          },
          changed: [],
          loading: true,
        },
        () => {
          const fetchOptions =
            type === "GT"
              ? newGameTitle
              : type === "MF"
                ? newManufacturer
                : type === "SP"
                  ? newSupplier
                  : type === "CT"
                    ? newCabinetType
                    : type === "DP"
                      ? newDepot
                      : type === "NA"
                        ? newNoteAcceptorModel
                        : newCategory;
          fetchOptions(operatorId)
            .then((data) => {
              const options = data?.main
                ? data
                : { main: data || [], ownership: [], operational: [] };
              this.setState({ selectedOptions: options });
            })
            .finally(() => this.setState({ loading: false }));
        },
      );
      return;
    }

    if (this.state.loading || this.state.selectedOptions.main.length) return;
    this.setState({ loading: true });
    const fetchOptions =
      type === "GT"
        ? newGameTitle
        : type === "MF"
          ? newManufacturer
          : type === "SP"
            ? newSupplier
            : type === "CT"
              ? newCabinetType
              : type === "DP"
                ? newDepot
                : type === "NA"
                  ? newNoteAcceptorModel
                  : newCategory;
    fetchOptions(operatorId)
      .then((data) => {
        const options = data?.main
          ? data
          : { main: data || [], ownership: [], operational: [] };
        this.setState({ selectedOptions: options });
      })
      .finally(() => this.setState({ loading: false }));
  }

  clearForm = () => {
    this.setState({
      selectedOptions: {
        main: [],
        ownership: [],
        operational: [],
      },
      changed: [],
      loading: false,
      saving: false,
    });
  };

  handleClose = () => {
    this.clearForm();
    this.props.onClose({ open: false });
  };

  handleSave = () => {
    const { operatorId, type, onClose, showSuccessAlert } = this.props;
    if (!this.isAllValid(this.state.selectedOptions)) return;
    this.setState({ saving: true });
    const payload = this.getAll(this.state.selectedOptions);
    const isGameTitle = type === "GT";
    const isManufacturer = type === "MF";
    const isSupplier = type === "SP";
    const isCabinetType = type === "CT";
    const isDepot = type === "DP";
    const isNoteAcceptor = type === "NA";
    const updateApi = isGameTitle
      ? updateGameTitle
      : isManufacturer
        ? updateManufacturer
        : isSupplier
          ? updateSupplier
          : isCabinetType
            ? (id, opId, details, cb) =>
                updateCabinetType(id, opId, details, false, cb ?? (() => {}))
            : isDepot
              ? updateDepot
              : isNoteAcceptor
                ? updateNoteAcceptorModel
                : updateCategory;
    const successMessage = isGameTitle
      ? "Game title created successfully."
      : isManufacturer
        ? "Manufacturer created successfully."
        : isSupplier
          ? "Supplier created successfully."
          : isCabinetType
            ? "Cabinet type created successfully."
            : isDepot
              ? "Depot created successfully."
              : isNoteAcceptor
                ? "Note acceptor created successfully."
                : "Category created successfully.";
    const lastCreatedType = isGameTitle
      ? "GT"
      : isManufacturer
        ? "MF"
        : isSupplier
          ? "SP"
          : isCabinetType
            ? "CT"
            : isDepot
              ? "DP"
              : isNoteAcceptor
                ? "NA"
                : "MT";
    updateApi(0, operatorId, payload, () => {})
      .then(() => {
        showSuccessAlert(successMessage);
        this.clearForm();
        onClose({ open: false, lastCreatedType });
      })
      .finally(() => this.setState({ saving: false }));
  };

  render() {
    const { open, name } = this.props;
    const { selectedOptions, loading, saving } = this.state;

    const mainOptions = this.mapChanged(selectedOptions.main);

    return (
      <Modal open={open} onClose={this.handleClose} size="small" closeIcon>
        <Modal.Header>
          {name}
        </Modal.Header>
        <Modal.Content>
            <Grid>
              <Grid.Row>
                <Grid.Column width={16}>
                  <OptionsList
                    options={mainOptions}
                    onChange={this.handleChangeValue}
                    loading={loading}
                  />
                </Grid.Column>
              </Grid.Row>              
            </Grid>
        </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleClose}>Cancel</Button>
            <Button
              positive
              icon="save"
              content="Create"
              loading={saving}
              disabled={!this.isAllValid(selectedOptions)}
              onClick={this.handleSave}
            />
          </Modal.Actions>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => {
  const modal = state.createAssetModal ?? {};
  return {
    open: modal.open ?? false,
    type: modal.type,
    operatorId: modal.operatorId,
    name: modal.name,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onClose: (payload) => dispatch(setCreateAssetModal(payload)),
  showSuccessAlert: (message, header = "Success") =>
    dispatch(showSuccessAlertAction(message, header)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateAssetModal);
